/**
 * ============================================================================
 * BLACK FRIDAY MODAL COMPONENT
 * ============================================================================
 * 
 * TEMPORARY COMPONENT - Remove after Black Friday sale ends
 * 
 * To remove this feature:
 * 1. Delete this file (BlackFridayModal.js)
 * 2. Delete BlackFridayPage.js in this folder
 * 3. Remove import and <BlackFridayModal /> from Home.js
 * 4. Remove the /blackfriday route from App.js
 * 5. Delete the blackfriday.css styles from App.css (marked section)
 * 6. Delete blackfriday folder from /src/components/
 * 
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function BlackFridayModal() {
  const [showModal, setShowModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState({});

  // Sale ends Cyber Monday at midnight EST (Dec 1, 2025 11:59:59 PM EST)
  const saleEndDate = new Date('2025-12-02T04:59:59Z');

  // Calculate time remaining
  const calculateTimeLeft = () => {
    const difference = saleEndDate - new Date();
    
    if (difference > 0) {
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    }
    return {};
  };

  // Show modal after delay - shows every page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowModal(true);
    }, 1500); // 1.5 second delay

    return () => clearTimeout(timer);
  }, []);

  // Update countdown every second
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    setTimeLeft(calculateTimeLeft());
    return () => clearInterval(timer);
  }, []);

  const closeModal = () => {
    setShowModal(false);
  };

  // Extend overlay to cover iOS safe areas (Dynamic Island / home bar)
  const safeAreaOverlayStyle = {
    position: 'fixed',
    top: 'calc(env(safe-area-inset-top, 0px) * -1)',
    right: 'calc(env(safe-area-inset-right, 0px) * -1)',
    bottom: 'calc(env(safe-area-inset-bottom, 0px) * -1)',
    left: 'calc(env(safe-area-inset-left, 0px) * -1)',
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)',
    paddingRight: 'calc(env(safe-area-inset-right, 0px) + 16px)',
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
    paddingLeft: 'calc(env(safe-area-inset-left, 0px) + 16px)',
  };

  // Don't show if sale has ended
  if (Object.keys(timeLeft).length === 0 && showModal) {
    return null;
  }

  return (
    <>
      {showModal && (
        <div 
          className="bf-modal-overlay relative z-50 flex items-center justify-center"
          style={safeAreaOverlayStyle}
          onClick={closeModal}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
          
          {/* Modal Content - Horizontal on desktop, vertical on mobile */}
          <div 
            className="bf-modal relative bg-white rounded-2xl shadow-2xl w-full max-w-md md:max-w-3xl overflow-hidden animate-bounce-in flex flex-col md:flex-row"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button 
              onClick={closeModal}
              className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/20 hover:bg-black/40 transition-colors text-white md:text-slate-600 text-xl font-bold"
              aria-label="Close"
            >
              ×
            </button>

            {/* Left Column - Image (on desktop) / Top (on mobile) */}
            <div className="relative w-full md:w-1/2 h-48 md:h-auto md:min-h-[400px] overflow-hidden">
              <img 
                src="/blackfriday.webp" 
                alt="Black Friday Sale" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-black/50 via-black/20 to-transparent"></div>
              
              {/* Limited Badge */}
              <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold animate-pulse">
                🔥 LIMITED TIME
              </div>
            </div>

            {/* Right Column - Content (on desktop) / Bottom (on mobile) */}
            <div className="w-full md:w-1/2 p-6 md:p-8 flex flex-col justify-center">
              {/* Logo */}
              <img 
                src="/newlogo.webp" 
                alt="MyK9 Logo" 
                className="hidden md:block h-[78px] w-auto object-contain mx-0 mb-4"
              />

              {/* Title */}
              <h2 className="text-3xl md:text-4xl font-Lobster text-[#57a8cc] mb-1 text-center md:text-left">
                Black Friday
              </h2>
              <p className="text-2xl md:text-3xl font-black text-slate-800 mb-3 text-center md:text-left">
                50% OFF Gift Cards!
              </p>

              {/* Subtitle */}
              <p className="text-slate-600 mb-4 text-center md:text-left">
                Give the gift of happy pups! Perfect for daycare, grooming & more 🐕
              </p>

              {/* Countdown Timer */}
              <div className="bg-slate-100 rounded-xl p-3 mb-4">
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 text-center">Sale ends in</p>
                <div className="flex justify-center gap-3">
                  {timeLeft.days !== undefined && (
                    <div className="bf-countdown-item">
                      <span className="text-xl md:text-2xl font-bold text-[#57a8cc]">{timeLeft.days}</span>
                      <span className="text-xs text-slate-500 block">DAYS</span>
                    </div>
                  )}
                  <div className="bf-countdown-item">
                    <span className="text-xl md:text-2xl font-bold text-[#57a8cc]">{String(timeLeft.hours || 0).padStart(2, '0')}</span>
                    <span className="text-xs text-slate-500 block">HRS</span>
                  </div>
                  <div className="bf-countdown-item">
                    <span className="text-xl md:text-2xl font-bold text-[#57a8cc]">{String(timeLeft.minutes || 0).padStart(2, '0')}</span>
                    <span className="text-xs text-slate-500 block">MIN</span>
                  </div>
                  <div className="bf-countdown-item">
                    <span className="text-xl md:text-2xl font-bold text-[#57a8cc]">{String(timeLeft.seconds || 0).padStart(2, '0')}</span>
                    <span className="text-xs text-slate-500 block">SEC</span>
                  </div>
                </div>
              </div>

              {/* Availability Notice */}
              <p className="text-sm text-red-500 font-medium mb-4 text-center md:text-left">
                ⚡ Limited availability at this price!
              </p>

              {/* CTA Button */}
              <Link 
                to="/blackfriday"
                onClick={closeModal}
                className="bf-cta-button block w-full bg-[#57a8cc] hover:bg-[#4a95b6] text-white font-bold py-4 px-6 rounded-xl text-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg no-underline text-center"
              >
                🎁 Get 50% Off Now
              </Link>

              {/* Skip Link */}
              <button 
                onClick={closeModal}
                className="mt-3 text-sm text-slate-400 hover:text-slate-600 transition-colors"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default BlackFridayModal;
